/** @format */

import { useState, useCallback } from 'react';
import * as listedLeadService from '../services/listedLeadsService';
import { ListedLeadFormInput } from '../types/listedLeads.types';

interface UseListedLeadFormReturn {
  listedLeads: ListedLeadFormInput[];
  loading: boolean;
  error: string | null;
  success: boolean;
  addListedLead: () => void;
  removeListedLead: (index: number) => void;
  updateListedLead: (index: number, listedLead: Partial<ListedLeadFormInput>) => void;
  submitListedLeads: () => Promise<boolean>;
  resetForm: () => void;
  clearMessage: () => void;
}

/**
 * Custom hook for listed lead form state management
 */
export const useListedLeadForm = (): UseListedLeadFormReturn => {
  const [listedLeads, setListedLeads] = useState<ListedLeadFormInput[]>([
    {
      listId: '',
      listName: '',
      officeId: '',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const addListedLead = useCallback(() => {
    setListedLeads((prev) => [
      ...prev,
      {
        listId: '',
        listName: '',
        officeId: '',
      },
    ]);
  }, []);

  const removeListedLead = useCallback((index: number) => {
    setListedLeads((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateListedLead = useCallback((index: number, listedLead: Partial<ListedLeadFormInput>) => {
    setListedLeads((prev) => {
      const newListedLeads = [...prev];
      newListedLeads[index] = { ...newListedLeads[index], ...listedLead };
      return newListedLeads;
    });
  }, []);

  const resetForm = useCallback(() => {
    setListedLeads([
      {
        listId: '',
        listName: '',
        officeId: '',
      },
    ]);
    setError(null);
    setSuccess(false);
  }, []);

  const submitListedLeads = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Filter out listed leads with empty required fields
      const validListedLeads = listedLeads.filter(
        (ll) => ll.listId.trim() !== '' && ll.listName.trim() !== '' && ll.officeId.trim() !== ''
      );

      if (validListedLeads.length === 0) {
        setError('Please add at least one listed lead with listId, listName, and officeId');
        setLoading(false);
        return false;
      }

      // Use batch endpoint even for single listed lead
      await listedLeadService.batchCreateListedLeads(validListedLeads);
      setSuccess(true);
      resetForm();
      return true;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error?.error ||
        'Failed to submit listed leads';
      setError(errorMessage);
      console.error('Error submitting listed leads:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [listedLeads, resetForm]);

  const clearMessage = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return {
    listedLeads,
    loading,
    error,
    success,
    addListedLead,
    removeListedLead,
    updateListedLead,
    submitListedLeads,
    resetForm,
    clearMessage,
  };
};
